import { useEffect, useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import debounce from "lodash.debounce";
import { Icons, Images } from "assets";
import { COLLABORATOR_ROLES } from "shared/utils/constants";
import {
  SearchCollaboratorService,
} from "shared/services/user";
import { collaboratorValidation } from "shared/utils/validation";
import classNames from "classnames";
import CustomInput from "shared/components/common/customInput";
import CustomAutoComplete from "shared/components/common/customAutoComplete";
import PermissionCard from "shared/components/permissionCard";
import styles from "./style.module.scss";
import CustomButton from "shared/components/common/customButton";
import CustomBadge from "shared/components/common/customBadge";
import CustomDeleteButton from "shared/components/common/customDeleteButton";
import CustomPhoneInput from "shared/components/common/customPhoneInput";
import CustomCheckbox from "shared/components/common/customCheckbox";
import {
  CollaboratorItem,
  deriveCollaboratorAccess,
  existingViewOnlyIds,
} from "shared/utils/collaboratorAccess";

interface StepOneProps {
  data: any;
  updateData: (data: any) => void;
  errors?: Record<string, string>;
  onNext?: () => void;
  onPrev?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const emptyForm: CollaboratorItem = {
  id: "",
  name: "",
  email: "",
  phone: "",
  role: null,
  viewOnly: false,
};

const StepOne = ({ data, updateData, errors = {} }: StepOneProps) => {
  const auth = useSelector((state: any) => state.root.auth);

  const initialCollaboratorIds = data?.collaborator_ids || [];
  const existingCollaboratorsFromData = (data?.collaborators || []).filter(
    (item: CollaboratorItem) =>
      item.isExisting && item.id?.startsWith("existing-"),
  );
  const idsFromExistingCollaborators = existingCollaboratorsFromData
    .map((item: CollaboratorItem) => {
      const idStr = item.id.replace("existing-", "");
      return parseInt(idStr, 10);
    })
    .filter((id: number) => !isNaN(id));

  const idsFromOnboardingProject = (
    auth?.onboarding_project?.collaborators || []
  )
    .map((c: any) => c.user_id || c.id)
    .filter((id: any) => typeof id === "number" && !isNaN(id));

  const [selectedUserIds, setSelectedUserIds] = useState<number[]>(
    initialCollaboratorIds.length > 0
      ? initialCollaboratorIds
      : idsFromExistingCollaborators.length > 0
        ? idsFromExistingCollaborators
        : idsFromOnboardingProject,
  );
  const projectName = auth?.onboarding_project?.name || "";
  const projectAsset =
    auth?.onboarding_project?.project_asset?.project_asset?.path ||
    Images.HomeImg2;
  const [currentAddress, setCurrentAddress] = useState(
    data?.currentAddress || "",
  );
  const [billingAddress, setBillingAddress] = useState(
    data?.billingAddress || "",
  );

  const [collabErrors, setCollabErrors] = useState<Record<string, string>>({});
  const [formKey, setFormKey] = useState(0);
  const newItemCounterRef = useRef(0);

  const mapProjectCollaborator = (
    pc: any,
  ): CollaboratorItem | null => {
    if (!pc) return null;
    const userId = pc.user_id || pc.id;
    if (!userId) return null;
    const { role, viewOnly } = deriveCollaboratorAccess(pc);
    return {
      id: `existing-${userId}`,
      name: pc.display_name || "",
      email: pc.email || "",
      role,
      viewOnly,
      user_type: pc.user_type,
      isExisting: true,
      profile_asset: pc.profile_asset?.path || pc.profile_asset || null,
    };
  };

  const onboardingProjectCollaborators: CollaboratorItem[] = (
    auth?.onboarding_project?.collaborators || []
  )
    .map(mapProjectCollaborator)
    .filter(
      (item: CollaboratorItem | null): item is CollaboratorItem =>
        item !== null,
    );

  const [listedItems, setListedItems] = useState<CollaboratorItem[]>(() => {
    if (data?.collaborators && data.collaborators.length > 0) {
      return data.collaborators;
    }
    return onboardingProjectCollaborators;
  });
  const [form, setForm] = useState<CollaboratorItem>(emptyForm);
  const [showForm, setShowForm] = useState<boolean>(() => {
    const initial =
      data?.collaborators && data.collaborators.length > 0
        ? data.collaborators
        : onboardingProjectCollaborators;
    return initial.length === 0;
  });

  const validateForm = async (): Promise<boolean> => {
    if (!showForm) {
      return true;
    }
    try {
      await collaboratorValidation.validate(form, { abortEarly: false });
      setCollabErrors({});
      return true;
    } catch (err: any) {
      const newErrors: Record<string, string> = {};
      err.inner?.forEach((e: any) => {
        if (e.path) newErrors[e.path] = e.message;
      });
      setCollabErrors(newErrors);
      return false;
    }
  };

  const handleAdd = async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    debouncedSearchUser.cancel();

    const emailLower = (form.email || "").trim().toLowerCase();
    const duplicateEmail = listedItems.some(
      (item) => (item.email || "").trim().toLowerCase() === emailLower,
    );
    if (duplicateEmail) {
      setCollabErrors((prev) => ({
        ...prev,
        email: "Already added.",
      }));
      return;
    }

    const useExisting =
      form.role &&
      userSearchStatus === "found" &&
      foundUser &&
      typeof foundUser.user_id === "number" &&
      (foundUser.email || "").trim().toLowerCase() === emailLower;

    if (useExisting && foundUser) {
      const userId = foundUser.user_id;
      setListedItems((prev) => [
        ...prev,
        {
          id: `existing-${userId}`,
          name: foundUser.display_name,
          email: foundUser.email,
          phone: foundUser.phone || "",
          role: form.role,
          viewOnly: form.role === "collaborator" && form.viewOnly === true,
          user_type: form.role || "collaborator",
          isExisting: true,
          profile_asset: null,
        },
      ]);
      setSelectedUserIds((prev) =>
        prev.includes(userId) ? prev : [...prev, userId],
      );
    } else {
      newItemCounterRef.current += 1;
      const newId = `new-${Date.now()}-${newItemCounterRef.current}`;
      setListedItems((prev) => [
        ...prev,
        {
          ...form,
          id: newId,
          viewOnly: form.role === "collaborator" && form.viewOnly === true,
        },
      ]);
    }

    setForm(emptyForm);
    setCollabErrors({});
    setUserSearchStatus("idle");
    setFoundUser(null);
    autoPopulatedRef.current = null;
    setFormKey((prev) => prev + 1);
    setShowForm(false);
  };

  const handleAddMore = () => {
    setShowForm(true);
    setCollabErrors({});
  };

  const handleViewOnlyToggle = (id: string) => {
    setListedItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, viewOnly: !item.viewOnly } : item,
      ),
    );
  };

  const handleDelete = (id: string) => {
    setListedItems((prev) => prev.filter((item) => item.id !== id));

    if (id.startsWith("existing-")) {
      const userId = parseInt(id.replace("existing-", ""));
      setSelectedUserIds((prev) => prev.filter((uid) => uid !== userId));
    }
  };

  const handleFormChange = (
    field: "name" | "email" | "phone",
    value: string,
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  type UserSearchStatus = "idle" | "searching" | "found" | "notFound";
  const [userSearchStatus, setUserSearchStatus] =
    useState<UserSearchStatus>("idle");
  const [foundUser, setFoundUser] = useState<{
    user_id: number;
    display_name: string;
    email: string;
    phone?: string;
  } | null>(null);
  const autoPopulatedRef = useRef<{ name: string; phone: string } | null>(null);

  const clearAutoPopulatedIfAny = () => {
    if (autoPopulatedRef.current) {
      setForm((prev) => ({ ...prev, name: "", phone: "" }));
      autoPopulatedRef.current = null;
    }
  };

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const debouncedSearchUser = useMemo(
    () =>
      debounce(async (email: string) => {
        try {
          const response = await SearchCollaboratorService(email);
          const result = response?.data?.data ?? response?.data;
          if (result?.found) {
            const newName = result.display_name || "";
            const newPhone = result.phone || "";
            setFoundUser({
              user_id: result.user_id,
              display_name: result.display_name,
              email: result.email,
              phone: result.phone,
            });
            setUserSearchStatus("found");
            setForm((prev) => ({
              ...prev,
              name: newName || prev.name,
              phone: newPhone || prev.phone,
            }));
            autoPopulatedRef.current = { name: newName, phone: newPhone };
          } else {
            clearAutoPopulatedIfAny();
            setFoundUser(null);
            setUserSearchStatus("notFound");
          }
        } catch {
          clearAutoPopulatedIfAny();
          setFoundUser(null);
          setUserSearchStatus("notFound");
        }
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearchUser.cancel();
    };
  }, [debouncedSearchUser]);

  useEffect(() => {
    if (!form.role) {
      clearAutoPopulatedIfAny();
      setUserSearchStatus("idle");
      setFoundUser(null);
      return;
    }
    const email = (form.email || "").trim();
    if (!email || !isValidEmail(email)) {
      clearAutoPopulatedIfAny();
      setUserSearchStatus("idle");
      setFoundUser(null);
      debouncedSearchUser.cancel();
      return;
    }
    setUserSearchStatus("searching");
    debouncedSearchUser(email);
  }, [form.email, form.role, debouncedSearchUser]);

  const handleRoleChange = (role: "collaborator" | "builder") => {
    setForm((prev) => ({
      ...prev,
      role: prev.role === role ? null : role,
    }));
    clearAutoPopulatedIfAny();
    setUserSearchStatus("idle");
    setFoundUser(null);
    if (collabErrors.role) {
      setCollabErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.role;
        return newErrors;
      });
    }
  };

  const getInitials = (name: string) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    return parts.length === 1
      ? parts[0][0].toUpperCase()
      : (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const accessLabel = (item: CollaboratorItem) => {
    if (item.role === "builder") return "Builder";
    return item.viewOnly ? "View only" : "Co-collaborator";
  };

  const prevValuesRef = useRef<string>("");

  useEffect(() => {
    const currentValuesString = `${projectName}|${currentAddress}|${billingAddress}|${
      listedItems.length
    }|${JSON.stringify(listedItems)}|${JSON.stringify(selectedUserIds)}`;

    const viewOnlyExistingIds = existingViewOnlyIds(
      listedItems,
      selectedUserIds,
    );

    if (prevValuesRef.current !== currentValuesString) {
      updateData({
        projectName,
        currentAddress,
        billingAddress,
        collaborators: listedItems,
        collaborator_ids: selectedUserIds,
        collaborator_view_only_ids: viewOnlyExistingIds,
      });
      prevValuesRef.current = currentValuesString;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectName,
    currentAddress,
    billingAddress,
    listedItems,
    selectedUserIds,
  ]);

  return (
    <div className={styles.cardWrapper}>
      <div
        className={classNames(styles.card, "mt-8 flex flex-col md:gap-5 gap-4")}
      >
        <div className={classNames(styles.projectAsset, "flex justify-center")}>
          <img src={projectAsset} alt={projectName || "Project"} />
        </div>

        <CustomInput
          label="Project Name"
          placeholder=""
          value={projectName}
          readOnly={true}
          error={errors.projectName}
          notRequiredMargin={true}
        />

        <CustomAutoComplete
          label="Current Address"
          required
          placeholder="Enter street address"
          value={currentAddress}
          formikKey="currentAddress"
          setFieldValue={(_, val) => setCurrentAddress(val)}
          error={errors.currentAddress}
          notRequiredMargin={true}
        />

        <CustomAutoComplete
          label="Billing Address"
          required
          placeholder="Enter street address"
          value={billingAddress}
          formikKey="billingAddress"
          setFieldValue={(_, val) => setBillingAddress(val)}
          error={errors.billingAddress}
          notRequiredMargin={true}
        />

        <div>
          <h6 className={styles.addCollaboratorTitle}>
            Add Collaborator <span>(Optional)</span>
          </h6>

          {listedItems.map((collab) => (
            <div key={collab.id} className={styles.displayCard}>
              <div className={styles.displayLeft}>
                <div className={styles.avatarContainer}>
                  {collab.profile_asset ? (
                    <img
                      src={collab.profile_asset}
                      alt={collab.name}
                      className={styles.profileImage}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget.nextElementSibling;
                        if (fallback) {
                          (fallback as HTMLElement).style.display = "flex";
                        }
                      }}
                    />
                  ) : null}
                  <div
                    className={styles.initialsAvatar}
                    style={{
                      display: collab.profile_asset ? "none" : "flex",
                    }}
                  >
                    {getInitials(collab.name)}
                  </div>
                </div>
                <div>
                  <div className={styles.nameRow}>
                    <h6>{collab.name}</h6>
                    <CustomBadge
                      textColor="#AD7636"
                      bg="#FDF6DD"
                      title={accessLabel(collab)}
                    />
                  </div>
                  <p>{collab.email}</p>
                  {collab.role === "collaborator" && (
                    <CustomCheckbox
                      name={`view-only-${collab.id}`}
                      label="View only"
                      checked={collab.viewOnly === true}
                      onChange={() => handleViewOnlyToggle(collab.id)}
                      labelClass={styles.viewOnlyToggleLabel}
                    />
                  )}
                </div>
              </div>

              <CustomDeleteButton
                onClick={() => handleDelete(collab.id)}
                ariaLabel={`Remove ${collab.name}`}
              />
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-3">
          {showForm && (
            <>
              <div className="flex flex-col gap-0.5">
                <div className="flex sm:flex-row flex-col gap-3">
                  {COLLABORATOR_ROLES.map(({ type, title, description }) => (
                    <PermissionCard
                      key={type}
                      title={title}
                      description={description}
                      selected={form.role === type}
                      onSelect={() => handleRoleChange(type)}
                      hasError={!!collabErrors.role}
                    >
                      {type === "collaborator" && form.role === type && (
                        <CustomCheckbox
                          name="new-collaborator-view-only"
                          label="View only"
                          checked={form.viewOnly === true}
                          onChange={() =>
                            setForm((prev) => ({
                              ...prev,
                              viewOnly: !prev.viewOnly,
                            }))
                          }
                          labelClass={styles.viewOnlyToggleLabel}
                        />
                      )}
                    </PermissionCard>
                  ))}
                </div>
                {collabErrors.role && (
                  <div className={styles.fieldError}>{collabErrors.role}</div>
                )}
              </div>

              <div className="flex flex-col ">
              <div
                key={formKey}
                className={classNames(
                  styles.collaboratorInputContainer,
                  "flex sm:flex-row flex-col xs:gap-3",
                )}
              >
                <div style={{ width: "100%" }}>
                  <CustomInput
                    placeholder="e.g. abc@example.com"
                    Icon={Icons.Mail}
                    customInputContainer={styles.collaboratorInput}
                    value={form.email}
                    onChange={(e: any) =>
                      handleFormChange("email", e.target.value)
                    }
                    error={collabErrors.email}
                  />
                </div>

                <CustomInput
                  placeholder="e.g. Sara Smith"
                  Icon={Icons.PersonIcon}
                  customInputContainer={styles.collaboratorInput}
                  value={form.name}
                  onChange={(e: any) =>
                    handleFormChange("name", e.target.value)
                  }
                  error={collabErrors.name}
                  readOnly={!!form.role && userSearchStatus === "found"}
                />
              </div>
              {form.role && (
                <p className={styles.builderSearchHint}>
                  {userSearchStatus === "searching" &&
                    "Searching for existing collaborator..."}
                  {userSearchStatus === "found" &&
                    "Existing collaborator found — name and phone auto-filled."}
                  {userSearchStatus === "notFound" &&
                    "No matching collaborator — a new one will be created."}
                </p>
              )}
              <CustomPhoneInput
                name="phone"
                value={form.phone || ""}
                onChange={(val) => handleFormChange("phone", val || "")}
                placeholder="Phone number (optional)"
                disable={!!form.role && userSearchStatus === "found"}
              />
              </div>
            </>
          )}

          {showForm ? (
            <div className="flex justify-end">
              <CustomButton
                title="Add"
                onClick={handleAdd}
                containerStyle={styles.addButton}
              />
            </div>
          ) : (
            listedItems.length > 0 && (
              <CustomButton
                title="Add More"
                onClick={handleAddMore}
                containerStyle={styles.buttonContainer}
                Icon={Icons.UserCircleAdd}
                IconDirection="left"
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default StepOne;
